import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  useQuery,
  useAction,
  getMyStudents,
  getMyInvitations,
  inviteStudent,
  revokeInvitation,
  resendInvitation,
  revokeRelation,
} from "wasp/client/operations";
import { useToast } from "../client/hooks/use-toast";
import { planHasPaidTier } from "../payment/plans";
import { trackInvitationSent } from "../client/utils/posthog";
import { Button } from "../client/components/ui/button";
import { Badge } from "../client/components/ui/badge";
import { Card, CardContent, CardHeader } from "../client/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../client/components/ui/dialog";
import { Input } from "../client/components/ui/input";
import useTranslation from "../client/hooks/useTranslation";
import { BulkImportModal } from "./BulkImportModal";
import { GraduationCap, Plus, Send, RotateCcw, X, Eye, Upload } from "lucide-react";

// NB: never hand-roll this list. The previous version used camelCase ids
// ("proAnnual"/"maxAnnual") that never matched the real values stored in DB
// ("pro_annual"/"max_annual"), so annual subscribers were denied the mentor
// program. planHasPaidTier is the single source of truth.

function statusClass(status: string): string {
  switch (status) {
    case "pending": return "border-yellow-500 text-yellow-500";
    case "accepted": return "bg-teal-600 text-white border-teal-600";
    case "declined": return "border-red-500 text-red-500";
    default: return "border-slate-500 text-slate-400";
  }
}

function UpgradePrompt() {
  const t = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
      <GraduationCap className="h-12 w-12 text-primary" />
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">{t.mentor.title}</h2>
        <p className="text-muted-foreground max-w-sm">{t.mentor.upgradeToBecomeMentor}</p>
      </div>
      <Link to="/account">
        <Button variant="default">{t.dashboard.upgradeButton}</Button>
      </Link>
    </div>
  );
}

function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useTranslation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inviteStudentAction = useAction(inviteStudent);

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      await inviteStudentAction({ email: email.trim().toLowerCase() });
      trackInvitationSent('single');
      toast({ title: t.mentor.invite.sendInvitation, description: email });
      setEmail("");
      onClose();
    } catch (err: any) {
      toast({ title: "Erreur", description: err?.message ?? "Une erreur est survenue.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.mentor.invite.title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <label className="text-sm font-medium text-foreground">{t.mentor.invite.emailLabel}</label>
          <Input
            type="email"
            placeholder={t.mentor.invite.inviteEmailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>{t.mentor.cancelInvitation}</Button>
          <Button onClick={handleSubmit} disabled={!email.trim() || isSubmitting}>
            <Send className="h-4 w-4 mr-2" />
            {t.mentor.invite.inviteSubmit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StudentsTab() {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: students = [], isLoading } = useQuery(getMyStudents);
  const revokeRelationAction = useAction(revokeRelation);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  const handleRevoke = async () => {
    if (!confirmRevokeId) return;
    try {
      await revokeRelationAction({ relationId: confirmRevokeId });
      toast({ title: t.mentor.revokeRelation });
    } catch (err: any) {
      toast({ title: "Erreur", description: err?.message, variant: "destructive" });
    } finally {
      setConfirmRevokeId(null);
    }
  };

  if (isLoading) return <div className="py-12 text-center text-muted-foreground text-sm">Chargement...</div>;

  if (students.length === 0) {
    return (
      <div className="py-16 text-center text-muted-foreground text-sm">
        <GraduationCap className="mx-auto mb-3 h-8 w-8 opacity-30" />
        {t.mentor.noStudents}
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-left">
              <th className="px-4 py-3 font-medium text-muted-foreground">Nom</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Email</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Depuis</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Accès</th>
              <th className="px-4 py-3 font-medium text-muted-foreground text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map((rel) => {
              const name = [rel.student.firstName, rel.student.lastName].filter(Boolean).join(" ") || rel.student.email || rel.studentId;
              const perms = rel.permissions;
              const accessCount = perms
                ? [perms.canViewJournal, perms.canViewDashboard, perms.canViewAnalytics, perms.canViewSessions, perms.canViewPropFirmManager].filter(Boolean).length
                : 5;
              return (
                <tr key={rel.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground">{name}</td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{rel.student.email ?? "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{new Date(rel.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{accessCount}/5 sections</Badge></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/mentor/student/${rel.studentId}`}>
                        <Button size="sm" variant="outline">
                          <Eye className="h-3.5 w-3.5 mr-1.5" />{t.mentor.viewStudent}
                        </Button>
                      </Link>
                      <Button size="sm" variant="destructive" onClick={() => setConfirmRevokeId(rel.id)}>
                        <X className="h-3.5 w-3.5 mr-1.5" />{t.mentor.revokeRelation}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Dialog open={confirmRevokeId !== null} onOpenChange={(v) => !v && setConfirmRevokeId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t.mentor.revokeRelation}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t.mentor.revokeConfirm}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevokeId(null)}>{t.mentor.cancelInvitation}</Button>
            <Button variant="destructive" onClick={handleRevoke}>{t.mentor.revokeRelation}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InvitationsTab() {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: invitations = [], isLoading } = useQuery(getMyInvitations);
  const revokeInvitationAction = useAction(revokeInvitation);
  const resendInvitationAction = useAction(resendInvitation);

  const handleRevoke = async (id: string) => {
    try {
      await revokeInvitationAction({ invitationId: id });
      toast({ title: t.mentor.cancelInvitation });
    } catch (err: any) {
      toast({ title: "Erreur", description: err?.message, variant: "destructive" });
    }
  };

  const handleResend = async (id: string) => {
    try {
      await resendInvitationAction({ invitationId: id });
      toast({ title: t.mentor.resendInvitation, description: "Email renvoyé !" });
    } catch (err: any) {
      toast({ title: "Erreur", description: err?.message, variant: "destructive" });
    }
  };

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      pending: t.mentor.status.pending,
      accepted: t.mentor.status.accepted,
      declined: t.mentor.status.declined,
      revoked: t.mentor.status.revoked,
      expired: t.mentor.status.expired,
    };
    return map[status] ?? status;
  };

  if (isLoading) return <div className="py-12 text-center text-muted-foreground text-sm">Chargement...</div>;

  if (invitations.length === 0) {
    return <div className="py-16 text-center text-muted-foreground text-sm">{t.mentor.noInvitations}</div>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30 text-left">
            <th className="px-4 py-3 font-medium text-muted-foreground">Email</th>
            <th className="px-4 py-3 font-medium text-muted-foreground">Statut</th>
            <th className="px-4 py-3 font-medium text-muted-foreground">Date</th>
            <th className="px-4 py-3 font-medium text-muted-foreground text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((inv) => (
            <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3 font-mono text-xs text-foreground">{inv.inviteeEmail}</td>
              <td className="px-4 py-3">
                <Badge variant="outline" className={statusClass(inv.status)}>{statusLabel(inv.status)}</Badge>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{new Date(inv.createdAt).toLocaleDateString()}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  {(inv.status === "pending" || inv.status === "expired") && (
                    <Button size="sm" variant="outline" onClick={() => handleResend(inv.id)}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{t.mentor.resendInvitation}
                    </Button>
                  )}
                  {inv.status === "pending" && (
                    <Button size="sm" variant="ghost" onClick={() => handleRevoke(inv.id)}>
                      <X className="h-3.5 w-3.5 mr-1.5" />{t.mentor.cancelInvitation}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type Tab = "students" | "invitations";

export default function MentorDashboardPage() {
  const t = useTranslation();
  const { data: user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("students");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const { data: students = [], refetch: refetchStudents } = useQuery(getMyStudents);
  const { data: invitations = [], refetch: refetchInvitations } = useQuery(getMyInvitations);

  if (!user) return null;

  const plan = (user as any).subscriptionPlan as string | null;
  const canUseMentorProgram = planHasPaidTier(plan);

  if (!canUseMentorProgram) return <UpgradePrompt />;

  const allEmails: string[] = [
    ...(students as any[]).map((s) => s.student.email as string).filter(Boolean),
    ...(invitations as any[])
      .filter((i) => i.status === "pending")
      .map((i) => i.inviteeEmail as string),
  ];

  return (
    <div className="space-y-6 py-2">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-primary" />
            {t.mentor.title}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{t.mentor.dashboard}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} className="gap-1.5">
            <Upload className="h-4 w-4" />
            {t.mentor.invite.importList}
          </Button>
          <Button onClick={() => setInviteOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t.mentor.invite.inviteButton}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <div className="flex gap-1 border-b border-border">
            {(["students", "invitations"] as Tab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab === "students" ? t.mentor.myStudents : t.mentor.myInvitations}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {activeTab === "students" ? <StudentsTab /> : <InvitationsTab />}
        </CardContent>
      </Card>

      <InviteDialog open={inviteOpen} onClose={() => { setInviteOpen(false); refetchInvitations(); }} />

      <BulkImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        existingEmails={allEmails}
        onImported={() => { refetchInvitations(); refetchStudents(); }}
      />
    </div>
  );
}
